import React from "react";
import { Row, Col, Button, ProgressBar } from "react-bootstrap";
import UsersSearch from "./UsersSearch";
import { FiltersIcon, LineIcon, CloseIcon, DownIconNew, NumberBoxIcon } from "../Global/Icons";
import { Badge, Dropdown, Form } from "react-bootstrap";

interface UsersTableHeaderFiltersProps {
  filtersEnabled: boolean;
  setFiltersEnabled: (v: boolean) => void;
  search: string;
  setSearch: (v: string) => void;
  setPage: (p: number) => void;
  selectedRoles: string[];
  selectedProjects: string[];
  roleOptions: string[];
  projectOptions: string[];
  toggleRole: (role: string) => void;
  toggleProject: (proj: string) => void;
  clearRoles: () => void;
  clearProjects: () => void;
  clearAllFilters: () => void;
  hideClass: string;
  getRoleClass: (role: string) => string;
  getProjectName: (id: string) => string | null;
  userLimitInfo?: { currentUsers: number; maxUsers: number } | null;
}

const UsersTableHeaderFilters: React.FC<UsersTableHeaderFiltersProps> = ({
  filtersEnabled,
  setFiltersEnabled,
  search,
  setSearch,
  setPage,
  selectedRoles,
  selectedProjects,
  roleOptions,
  projectOptions,
  toggleRole,
  toggleProject,
  clearRoles,
  clearProjects,
  clearAllFilters,
  hideClass,
  getRoleClass,
  getProjectName,
  userLimitInfo,
}) => (
  <>
    <thead>
      <tr>
        <th colSpan={100} className="text-end">
          <Row>
            <Col md={7} lg={8}>
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="link"
                  onClick={() => setFiltersEnabled(!filtersEnabled)}
                  aria-pressed={filtersEnabled}
                  title="Toggle filters"
                  className={(filtersEnabled ? "text-secondary " : "text-muted ") + "align-items-center p-0"}
                >
                  <FiltersIcon width={19} style={{ marginTop: -4 }} />
                  <span className="ms-1">Filters</span>
                </Button>
                <LineIcon className="text-muted mx-2" />
                <div style={{ flex: 1 }}>
                  <UsersSearch
                    value={search}
                    onChange={(v) => {
                      setSearch(v);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
            </Col>
            <Col md={5} lg={4} className="d-flex justify-content-end align-items-center gap-3">
              <div className="flex-column text-end d-none d-lg-flex" style={{ minWidth: 120 }}>
                <div className="text-muted mb-2 pb-1">
                  {userLimitInfo?.currentUsers}/{userLimitInfo?.maxUsers} Users
                </div>
                <ProgressBar variant="danger" now={
                  userLimitInfo
                    ? (userLimitInfo.currentUsers / userLimitInfo.maxUsers) * 100
                    : 0
                } />
              </div>
            </Col>
          </Row>
        </th>
      </tr>
    </thead>
    <thead className="bg-lighter">
      <tr>
        <th>User</th>
        <th>
          <div className="d-flex align-items-center gap-2">
            <span className={selectedRoles.length > 0 ? "text-danger" : ""}>Roles</span>
            <div className={hideClass + " d-flex align-items-center"}>
              <Dropdown autoClose>
                <Dropdown.Toggle
                  variant="link"
                  className={(selectedRoles.length > 0 ? "text-danger " : "") + "p-0 text-dark no-caret d-flex align-items-center gap-1"}
                >
                  <NumberBoxIcon className={selectedRoles.length > 0 ? "text-danger " : ""} number={selectedRoles.length} />
                  <span className="mx-1">Selected</span>
                  <DownIconNew />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: 200 }} className="shadow border">
                  <div className="pt-4 px-4">
                    {roleOptions.map((r) => {
                      const checked = selectedRoles.includes(r);
                      return (
                        <div
                          key={r}
                          className="d-flex align-items-center pb-4"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRole(r);
                          }}
                          style={{ cursor: "pointer", justifyContent: "flex-start" }}
                        >
                          <div className="me-3">
                            <Form.Check type="checkbox" id={`role-${r}`} checked={checked} readOnly />
                          </div>
                          <div>
                            <Badge className={getRoleClass(r) + " role"}>{r}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Dropdown.Divider />
                  <div className="p-4 pt-3">
                    <Button variant="link" onClick={clearRoles} className="p-0 text-danger">
                      Clear Selection
                    </Button>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              {selectedRoles.length > 0 && (
                <Button variant="link" className="text-danger p-0 ms-2" onClick={clearRoles}>
                  <CloseIcon />
                </Button>
              )}
            </div>
          </div>
        </th>
        <th>
          <div className="d-flex align-items-center gap-2">
            <span className={selectedProjects.length > 0 ? "text-danger" : ""}>Projects</span>
            <div className={hideClass + " d-flex align-items-center"}>
              <Dropdown align="end" autoClose="outside">
                <Dropdown.Toggle
                  variant="link"
                  className={(selectedProjects.length > 0 ? "text-danger " : "") + "p-0 text-dark no-caret d-flex align-items-center gap-1"}
                >
                  <NumberBoxIcon className={selectedProjects.length > 0 ? "text-danger " : ""} number={selectedProjects.length} />
                  <span className="mx-1">Selected</span>
                  <DownIconNew />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: 200 }} className="shadow border">
                  <div className="pt-4 px-4">
                    {projectOptions.map((p) => {
                      const checked = selectedProjects.includes(p);
                      return (
                        <div
                          key={p}
                          className="d-flex align-items-center pb-4"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProject(p);
                          }}
                          style={{ cursor: "pointer", justifyContent: "flex-start" }}
                        >
                          <div className="me-3">
                            <Form.Check type="checkbox" id={`proj-${p}`} checked={checked} readOnly />
                          </div>
                          <div>
                            <Badge bg="light" text="dark" className="project" style={{ border: "1px solid rgba(0,0,0,0.04)" }}>
                              {getProjectName(p)}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Dropdown.Divider />
                  <div className="p-4 pt-3">
                    <Button variant="link" onClick={clearProjects} className="p-0 text-danger">
                      Clear Selection
                    </Button>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              {selectedProjects.length > 0 && (
                <Button variant="link" className="text-danger p-0 ms-2" onClick={clearProjects}>
                  <CloseIcon />
                </Button>
              )}
            </div>
          </div>
        </th>
        <th colSpan={2} className="text-end">
          <div className={hideClass}>
            <Button variant="link" className="text-danger p-0 me-3" onClick={clearAllFilters}>
              <CloseIcon /> Clear All
            </Button>
          </div>
        </th>
      </tr>
    </thead>
  </>
);

export default UsersTableHeaderFilters;
